/**
 * Manutenção preventiva — lista equipamentos com manutenção próxima
 * e permite ao Supervisor agendar nova manutenção / registrar conclusão.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Wrench, CalendarClock, ClipboardCheck, Send } from 'lucide-react';
import { apiGet, apiPost, ApiError } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthProvider';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';

interface DueRow {
  id: string;
  equipment_id: string;
  equipment_code: string;
  equipment_name: string;
  kind: string;
  next_due_at: string;
  urgency: 'OK' | 'DUE_SOON' | 'OVERDUE';
}

const URG_TONE: Record<DueRow['urgency'], 'success' | 'warning' | 'danger'> = {
  OK: 'success', DUE_SOON: 'warning', OVERDUE: 'danger',
};
const URG_LABEL: Record<DueRow['urgency'], string> = {
  OK: 'No prazo', DUE_SOON: 'Próxima', OVERDUE: 'Vencida',
};

type Kind = 'PREVENTIVE' | 'CORRECTIVE' | 'CALIBRATION' | 'INSPECTION';

export function ManutencaoPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canSchedule = !!user && (user.role === 'ADMIN' || user.role === 'SUPERVISOR');

  const list = useQuery({
    queryKey: ['maintenance', 'due'],
    queryFn: () => apiGet<{ items: DueRow[] }>('/v1/dashboard/maintenance-due'),
    refetchInterval: 60_000,
  });

  const [target, setTarget] = useState<DueRow | null>(null);
  const [kind, setKind] = useState<Kind>('PREVENTIVE');
  const [scheduledAt, setScheduledAt] = useState('');
  const [technician, setTechnician] = useState('');
  const [company, setCompany] = useState('');
  const [nextDueAt, setNextDueAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const schedule = useMutation({
    mutationFn: () => apiPost<{ id: string }>(
      `/v1/equipments/${target!.equipment_id}/maintenances`,
      {
        kind,
        scheduled_at: scheduledAt || undefined,
        technician_name: technician || undefined,
        technician_company: company || undefined,
        next_due_at: nextDueAt || undefined,
      },
      { idempotencyKey: `maint-${target!.equipment_id}-${Date.now()}` },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['maintenance'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setTarget(null); setScheduledAt(''); setTechnician(''); setCompany(''); setNextDueAt(''); setError(null);
    },
    onError: (e) => {
      if (e instanceof ApiError) setError(`${e.code}: ${e.message}`);
      else setError((e as Error).message);
    },
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
          Manutenção preventiva
        </h1>
        <p className="text-sm text-neutral-500">
          Equipamentos com manutenção vencida ficam automaticamente bloqueados para novos lotes.
        </p>
      </header>

      <Card>
        <CardHeader title="Equipamentos com manutenção próxima ou vencida" subtitle={list.isLoading ? 'Carregando…' : `${list.data?.items.length ?? 0} pendentes`} />
        <CardBody className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500 dark:bg-neutral-700">
              <tr>
                <th className="px-4 py-2">Equipamento</th>
                <th className="px-4 py-2">Tipo</th>
                <th className="px-4 py-2">Próxima</th>
                <th className="px-4 py-2">Situação</th>
                <th className="px-4 py-2 text-right">Ação</th>
              </tr>
            </thead>
            <tbody>
              {(list.data?.items ?? []).map((m) => (
                <tr key={m.id} className={`border-t border-neutral-200 dark:border-neutral-700 ${m.urgency === 'OVERDUE' ? 'bg-danger-bg/30' : m.urgency === 'DUE_SOON' ? 'bg-warning-bg/30' : ''}`}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Wrench size={16} className="text-neutral-400" />
                      <div className="min-w-0">
                        <div className="font-medium">{m.equipment_name}</div>
                        <div className="font-mono text-xxs text-neutral-500">{m.equipment_code}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs">{m.kind}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    <span className="inline-flex items-center gap-1"><CalendarClock size={12} className="text-neutral-400" /> {m.next_due_at}</span>
                  </td>
                  <td className="px-4 py-2"><Badge tone={URG_TONE[m.urgency]}>{URG_LABEL[m.urgency]}</Badge></td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      leadingIcon={<ClipboardCheck size={14} />}
                      disabled={!canSchedule}
                      onClick={() => { setTarget(m); setKind('PREVENTIVE'); setError(null); }}
                    >
                      Agendar
                    </Button>
                  </td>
                </tr>
              ))}
              {!list.isLoading && (list.data?.items?.length ?? 0) === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-xs text-neutral-500">Nada pendente.</td></tr>
              ) : null}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {target ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-neutral-900/50 p-4">
          <div className="w-full max-w-md rounded border border-neutral-200 bg-neutral-0 shadow-md dark:bg-neutral-800 dark:border-neutral-700">
            <div className="border-b border-neutral-200 px-4 py-3 dark:border-neutral-700">
              <h2 className="text-md font-semibold">Agendar manutenção</h2>
              <p className="text-xs text-neutral-500">{target.equipment_name} · {target.equipment_code}</p>
            </div>
            <div className="space-y-3 p-4">
              <div>
                <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Tipo</label>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(['PREVENTIVE','CORRECTIVE','CALIBRATION','INSPECTION'] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setKind(k)}
                      className={`rounded border px-3 py-1.5 text-xs transition-colors ${kind === k ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200' : 'border-neutral-300 dark:border-neutral-700'}`}
                    >{k}</button>
                  ))}
                </div>
              </div>
              <Input label="Agendado para (ISO date)" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} placeholder="2026-06-15T14:00:00Z" />
              <Input label="Técnico responsável" value={technician} onChange={(e) => setTechnician(e.target.value)} />
              <Input label="Empresa" value={company} onChange={(e) => setCompany(e.target.value)} />
              <Input label="Próxima manutenção" value={nextDueAt} onChange={(e) => setNextDueAt(e.target.value)} placeholder="2027-06-15" />
              {error ? <p className="text-xs text-danger">{error}</p> : null}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-4 py-3 dark:border-neutral-700">
              <Button variant="ghost" onClick={() => setTarget(null)}>Cancelar</Button>
              <Button variant="primary" leadingIcon={<Send size={16} />} loading={schedule.isPending} onClick={() => schedule.mutate()}>
                Registrar
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
